import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

export function AuthVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    const token = searchParams.get('token');
    const redirectPath = searchParams.get('redirect');

    if (!token) {
      setStatus('error');
      setError('Geen token gevonden in de URL');
      return;
    }

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-magic-link`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verificatie mislukt');
      }

      if (!data.success || !data.hashed_token) {
        throw new Error('Ongeldige response van de server');
      }

      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: data.hashed_token,
        type: 'magiclink',
      });

      if (verifyError) {
        console.error('OTP verify error:', verifyError);
        throw new Error('Kan sessie niet aanmaken. Probeer opnieuw in te loggen.');
      }

      setStatus('success');
      setTimeout(() => {
        if (redirectPath) {
          navigate(redirectPath, { replace: true });
        } else if (data.is_support_user) {
          navigate('/support', { replace: true });
        } else if (data.is_admin) {
          navigate('/admin/dashboard', { replace: true });
        } else if (data.needs_onboarding) {
          navigate('/onboarding', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }, 400);
    } catch (err: any) {
      console.error('Verification error:', err);
      setStatus('error');
      setError(err.message || 'Er ging iets mis bij het verifiëren van je inloglink');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {status === 'verifying' && (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Loader className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-[#231F20] mb-2">
                Bezig met verifiëren...
              </h2>
              <p className="text-gray-600">
                Even geduld terwijl we je inloglink controleren
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-[#231F20] mb-2">
                Succesvol geverifieerd!
              </h2>
              <p className="text-gray-600">
                Een moment, we zetten alles voor je klaar...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-[#231F20] mb-2">
                Verificatie mislukt
              </h2>
              <p className="text-gray-600 mb-6">
                {error}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="btn-primary"
              >
                Terug naar login
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Door in te loggen ga je akkoord met onze{' '}
          <a href="https://www.dibbi.nl/documents/v1.1-algemene-voorwaarden-dibbi.pdf" className="underline hover:text-gray-700" target="_blank" rel="noopener noreferrer">
            voorwaarden
          </a>
        </p>
      </div>
    </div>
  );
}
